import React, { useRef, useState, useEffect } from "react"
import { ThemeProvider } from "styled-components"
import Observer from "@researchgate/react-intersection-observer"

import useTracking from "hooks/useTracking"
import componentThemeExtension from "utils/componentThemeExtension"

import StarsRating from "./stars-rating"

import {
  Container,
  ProductWrapper,
  InfoWrapper,
  MediaWrapper,
  ProductTestiomonalHeader,
  ProductTestimonialHeadline,
  TestimonialAttribution,
  Stars,
  ProductHeader,
  ProductHeadline,
  ClassShortIcon,
  Description,
  ButtonOne,
  VideoContainer,
  ClassImage,
  Button,
} from "./styles"

const Product = React.memo(
  ({ element, inline, setBottomStickyProduct, isLastElement, isPreLastElement }) => {
    const {
      title,
      trackProduct,
      productId,
      autoplacement,
      useTestimonial,
      classId,
      isClass,
      buttonText,
      url,
      sponsoredUrl,
      nofollowUrl,
      className,
      hidden,
    } = element

    // a missing title indicates a missing product
    if (!title) {
      return null
    }

    if (useTestimonial) {
      element.priority = 1
    }

    const { trackView, trackClick } = useTracking()
    const ref = useRef(null)

    const trackingId = `Article Inline Product | ${title}`
    const trackingProps = {
      label: trackingId,
      category: "Commerce Engagement",
      product: trackProduct,
      product_id: productId,
      autoplacement: Boolean(autoplacement),
      testimonial: Boolean(useTestimonial),
      class_id: classId,
    }

    trackView(ref, trackingId, trackingProps)
    trackClick(ref, trackingId, trackingProps)

    const rel =
      sponsoredUrl || productId
        ? "sponsored noreferrer"
        : nofollowUrl
        ? "noFollow noreferrer"
        : "noreferrer"

    const getDefaultButtonText = () => {
      if (!productId && !isClass) {
        return "Get started"
      }

      if (isClass) {
        return "Learn More"
      }

      return "Shop Now"
    }

    const handleObserverChange = (entry) => {
      if (isLastElement || isPreLastElement) {
        return
      }
      if (entry.boundingClientRect.bottom < 0) {
        setBottomStickyProduct(element)
      } else if (entry.intersectionRatio === 1) {
        setBottomStickyProduct(null)
      }
    }

    const productContent = React.useMemo(
      () => (
        <ProductWrapper ref={ref} id={productId} priority={element.priority} inline={inline}>
          <a href={url} rel={rel} target="_blank">
            <InfoWrapper priority={element.priority}>
              <ProductHeading {...element} />
              <ButtonOne
                priority={element.priority}
                custom={!productId && !isClass}
                isClass={isClass}
              >
                {buttonText || getDefaultButtonText()}
              </ButtonOne>
            </InfoWrapper>
          </a>
          <MediaWrapper>
            <Media url={url} rel={rel} {...element} />
          </MediaWrapper>
          <a href={url} rel={rel} target="_blank">
            <Button priority={element.priority} custom={!productId && !isClass} isClass={isClass}>
              {buttonText || getDefaultButtonText()}
            </Button>
          </a>
        </ProductWrapper>
      ),
      [element, productId, url, rel, buttonText, isClass, inline],
    )

    return (
      <Observer
        rootMargin="0% 0% -80%"
        onChange={handleObserverChange}
        disabled={autoplacement}
        threshold={[0, 0.8, 1]}
      >
        <Container
          className={className}
          data-exp-id="article-product"
          hidden={hidden}
          priority={element.priority}
        >
          {productContent}
        </Container>
      </Observer>
    )
  },
)

const ProductHeading = ({
  priority,
  title,
  productId,
  description,
  useTestimonial,
  testimonial,
  testimonialAttribution,
  totalReviews,
  ratingP,
}) => {
  if (priority === 1 && useTestimonial) {
    return (
      <ProductTestiomonalHeader>
        <ProductTestimonialHeadline
          priority={priority}
          dangerouslySetInnerHTML={{
            __html: testimonial,
          }}
        />
        <TestimonialAttribution
          priority={priority}
          dangerouslySetInnerHTML={{
            __html: testimonialAttribution,
          }}
        />
        <Stars>
          <div className="rating-stars">
            <div className="rating-stars__bottom">★ ★ ★ ★ ★</div>
            <div className="rating-stars__top">★ ★ ★ ★ ★</div>
          </div>{" "}
          <strong>({totalReviews})</strong>
        </Stars>
      </ProductTestiomonalHeader>
    )
  }

  return (
    <>
      <ProductHeader>
        <ProductHeadline
          priority={priority}
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
        {productId && <ClassShortIcon id="shop-icon" priority={priority} />}
      </ProductHeader>
      {description && (
        <Description
          priority={priority}
          dangerouslySetInnerHTML={{
            __html: `<object>${description}</object>`,
          }}
        />
      )}
      {ratingP && (
        <StarsRating
          rating={ratingP}
          renderOnMobile
          isArticleProduct
          totalReviews={totalReviews}
          color="#222"
        />
      )}
    </>
  )
}

const Media = ({
  priority,
  image,
  useTestimonial,
  testimonialVideoId,
  videoSignature,
  testimonialThumb,
  rel,
  url,
}) => {
  const imageRef = useRef(null)

  if (priority === 1 && useTestimonial && testimonialVideoId) {
    return (
      <ThemeProvider theme={componentThemeExtension(null)}>
        <VideoContainer
          id={`testimonialVideo_${testimonialVideoId}`}
          videoId={testimonialVideoId}
          videoSignature={videoSignature}
          thumbnail={testimonialThumb}
          fullWidth
        />
      </ThemeProvider>
    )
  }

  const transforms = [
    {
      screenSize: 768,
      transforms:
        priority === 1
          ? "c_fill,w_256,h_256,g_auto,q_auto,fl_lossy,f_auto" // square on desktop
          : "c_fit,w_228,q_auto,fl_lossy,f_auto",
    },
    {
      transforms: "c_fit,w_480,q_auto,fl_lossy,f_auto",
    },
  ]

  return image?.path ? (
    <a href={url} rel={rel} target="_blank" ref={imageRef}>
      <ClassImage
        priority={priority}
        baseUrl={image.path}
        images={transforms}
        contain
        loading="eager"
      />
    </a>
  ) : null
}

export default Product
